import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, Star, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { useSite } from "@/lib/site";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import PhoneShowcase from "@/components/PhoneShowcase";
import PricingTabs from "@/components/PricingTabs";
import { Reveal } from "@/components/Reveal";
import { Btn, IdentityLabel, MetaTag, SectionLabel } from "@/components/ui-bits";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const find = (sections, key) => (sections || []).find((s) => s.key === key);
const motionEase = [0.16, 1, 0.3, 1];

function ResponsiveCtaLabel({ text }) {
  const words = String(text || "").trim().split(/\s+/);
  const first = words.shift();
  const rest = words.join(" ");
  if (!rest) return text;
  return (
    <>
      <span className="hero-cta-label lg:hidden"><span className="block">{first}</span><span className="block">{rest}</span></span>
      <span className="hidden lg:inline">{text}</span>
    </>
  );
}

function HeroVisual({ hero, portfolio = [], className = "", compact = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.9, ease: motionEase }}
      className={`relative ${className}`}
      data-testid={compact ? "hero-visual-mobile" : "hero-visual-desktop"}
    >
      <div className={`grid grid-cols-2 ${compact ? "gap-3" : "gap-4"}`}>
        <div className="aspect-[3/4] overflow-hidden rounded-3xl border border-border soft-shadow">
          <img src={hero.image} alt="Konten social media Digitalis Medan" className="h-full w-full object-cover" loading="eager" />
        </div>
        <div className={`${compact ? "mt-7" : "mt-10"} aspect-[3/4] overflow-hidden rounded-3xl border border-border soft-shadow`}>
          <img src={hero.image_secondary || portfolio[0]?.cover_image || hero.image} alt="Portfolio digital marketing Medan" className="h-full w-full object-cover" loading="eager" />
        </div>
      </div>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className={`glass absolute left-1/2 flex -translate-x-1/2 items-center gap-3 border border-border soft-shadow ${compact ? "-bottom-4 rounded-xl px-3 py-2" : "-bottom-5 rounded-2xl px-5 py-3"}`}
      >
        <span className={`${compact ? "h-8 w-8 text-sm" : "h-9 w-9"} flex shrink-0 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground`}>D</span>
        <div className="min-w-0">
          <p className={`${compact ? "text-xs" : "text-sm"} whitespace-nowrap font-bold leading-tight`}>Digital Growth</p>
          <p className={`${compact ? "text-[10px]" : "text-xs"} whitespace-nowrap text-muted-foreground`}>Social · SEO · Creator</p>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Home() {
  const { seo } = useSite();
  const { data } = useQuery({ queryKey: ["home"], queryFn: async () => (await api.get("/home")).data });
  const [editorPreview, setEditorPreview] = useState(null);

  useEffect(() => {
    const receivePreview = (event) => {
      if (event.origin !== window.location.origin || event.data?.type !== "digitalis-home-preview") return;
      setEditorPreview(event.data);
    };
    window.addEventListener("message", receivePreview);
    return () => window.removeEventListener("message", receivePreview);
  }, []);

  const sections = (data?.sections || [])
    .map((section) => {
      const override = editorPreview?.sections?.[section.key];
      if (!override) return section;
      return { ...section, ...override, items: override.items || section.items };
    })
    .filter((section) => section.enabled !== false);
  const portfolio = data?.portfolio || [];
  const posts = data?.posts || [];
  const faqs = data?.faqs || [];
  const services = data?.services || [];
  const { data: team = [] } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/team")).data });

  const heroRecord = find(sections, "hero");
  const hero = heroRecord ? { ...heroRecord, ...(editorPreview?.hero || {}) } : heroRecord;
  const flagship = find(sections, "flagship");
  const trust = find(sections, "trust");
  const showcase = find(sections, "showcase");
  const servicesSec = find(sections, "services");
  const portfolioSec = find(sections, "portfolio");
  const why = find(sections, "why");
  const process = find(sections, "process");
  const micro = find(sections, "micro");
  const local = find(sections, "local");
  const blogSec = find(sections, "blog");
  const faqSec = find(sections, "faq");
  const cta = find(sections, "cta");
  const teamSec = find(sections, "team_section");
  const heroTitle = (hero?.title || "Digital Marketing Spesialist Medan untuk Brand yang Ingin Tumbuh").replace("Digital Marketing Agency", "Digital Marketing Spesialist");
  const homeSeoTitle = seo?.home_title?.replace("Digital Marketing Agency", "Digital Marketing Spesialist");

  const orgJsonLd = { "@context": "https://schema.org", "@type": "Organization", name: "Digitalis", description: seo?.site_description, areaServed: { "@type": "City", name: "Medan" } };
  const ease = motionEase;

  return (
    <div data-testid="home-page">
      <Seo title={homeSeoTitle} description={seo?.home_description} canonicalPath="/" image={seo?.default_share_image} jsonLd={orgJsonLd} />

      {/* HERO */}
      {hero && (
        <section className="relative overflow-hidden" data-testid="hero-section">
          <div className="relative mx-auto grid max-w-[1400px] items-center gap-12 px-5 pb-16 pt-10 md:px-10 md:pt-20 lg:grid-cols-2">
            <div>
              <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}
                className="mb-6" data-testid="hero-eyebrow">
                <IdentityLabel className="mb-0">{hero.eyebrow || "Digital Marketing Spesialist"}</IdentityLabel>
              </motion.div>
              <h1 className="font-display text-[2.05rem] font-extrabold leading-[1.05] tracking-tightest sm:text-4xl md:text-5xl lg:text-[3.9rem]" data-testid="hero-title">
                <motion.span initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease, delay: 0.05 }} className="block">
                  {heroTitle.split(" untuk")[0]}
                </motion.span>
                <motion.span initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease, delay: 0.18 }} className="block">
                  {heroTitle.includes("untuk") ? "untuk" + heroTitle.split(" untuk")[1] : ""}
                </motion.span>
              </h1>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="mt-5 text-base leading-relaxed text-muted-foreground lg:hidden"
                data-testid="hero-mobile-summary"
              >
                Strategi digital agar brand Anda lebih mudah ditemukan, dikenal, dan bertumbuh.
              </motion.p>
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35, duration: 0.6 }}
                className="mt-6 hidden max-w-lg text-lg leading-relaxed text-muted-foreground lg:block">{hero.subtitle}</motion.p>
              <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.6 }} className="mt-7 grid grid-cols-1 gap-3 min-[360px]:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)] lg:mt-8 lg:flex lg:flex-wrap lg:items-center">
                <Btn to={hero.cta_primary_link || "/contact"} className="hero-cta justify-center whitespace-nowrap" data-testid="hero-cta-primary"><ResponsiveCtaLabel text={hero.cta_primary} /></Btn>
                <Btn to={hero.cta_secondary_link || "/portfolio"} className="hero-cta justify-center whitespace-nowrap" variant="ghost" data-testid="hero-cta-secondary"><ResponsiveCtaLabel text={hero.cta_secondary} /></Btn>
              </motion.div>
              <HeroVisual hero={hero} portfolio={portfolio} compact className="mb-10 mt-8 lg:hidden" />
              <div className="mt-8 grid grid-cols-2 gap-3 text-xs text-muted-foreground sm:flex sm:items-center sm:gap-4 sm:text-sm lg:mt-10">
                <span className="inline-flex min-w-0 items-center gap-1.5 rounded-xl border border-border bg-card/60 px-3 py-2.5"><MapPin className="h-4 w-4 shrink-0 text-primary" /> {hero.location_signal}</span>
                <span className="inline-flex min-w-0 items-center gap-1.5 rounded-xl border border-border bg-card/60 px-3 py-2.5"><Star className="h-4 w-4 shrink-0 fill-primary text-primary" /> Studio kreatif & performa</span>
              </div>
            </div>

            <HeroVisual hero={hero} portfolio={portfolio} className="hidden lg:block" />
          </div>
        </section>
      )}

      {/* TRUST MARQUEE */}
      {trust && <section className="border-y border-border bg-surface/50 py-4 md:py-8" data-testid="trust-section" aria-label={trust.title || "Layanan Digitalis"}>
        <Marquee items={["Social Media Marketing", "Micro Influencer", "Google Maps", "SEO", "Followers", "Pembuatan Website", "Content Creator"]} />
      </section>}

      {/* 3D PHONE SHOWCASE */}
      {showcase && <PhoneShowcase section={showcase} />}

      {/* PRICING TABS = Layanan Kami */}
      {servicesSec && <PricingTabs homeSection={servicesSec} />}

      {/* FLAGSHIP */}
      {flagship && (
        <section className="py-16 md:py-32" data-testid="flagship-section">
          <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-6">
              <div className="relative rounded-3xl overflow-hidden aspect-[4/3] soft-shadow border border-border">
                <img src={flagship.image} alt="Social media marketing Medan oleh Digitalis" className="w-full h-full object-cover" loading="lazy" />
                <span className="absolute top-4 left-4 bg-primary text-primary-foreground text-xs font-bold px-3 py-1.5 rounded-full">✦ Flagship Service</span>
              </div>
            </div>
            <div className="lg:col-span-6">
              <SectionLabel num="01">Flagship</SectionLabel>
              <Reveal>
                <h2 className="font-display font-extrabold tracking-tightest leading-tight text-3xl md:text-4xl lg:text-5xl mb-6">{flagship.title}</h2>
                <p className="text-muted-foreground text-lg leading-relaxed mb-8 max-w-lg">{flagship.description}</p>
                <div className="grid grid-cols-2 gap-3 mb-10 max-w-md">
                  {(services.find((s) => s.flagship)?.benefits || []).map((b) => (
                    <div key={b} className="flex items-center gap-2 text-sm"><span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center"><Check className="w-3 h-3" strokeWidth={3} /></span>{b}</div>
                  ))}
                </div>
                <Btn to={flagship.cta_link}>{flagship.cta}</Btn>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* PORTFOLIO */}
      {portfolio.length > 0 && (
        <section className="py-16 md:py-32 bg-surface" data-testid="portfolio-section">
          <div className="mx-auto max-w-[1400px] px-5 md:px-10">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
              <div><SectionLabel num="02">Selected Work</SectionLabel>
                <h2 className="font-display font-extrabold tracking-tightest leading-tight text-4xl md:text-5xl">{portfolioSec?.title || "Karya Terpilih"}</h2>{portfolioSec?.subtitle && <p className="mt-3 max-w-xl text-muted-foreground">{portfolioSec.subtitle}</p>}</div>
              <Link to="/portfolio" className="link-underline flex min-h-11 items-center gap-1.5 text-sm font-semibold text-primary">Semua Karya <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-8 sm:gap-6 md:gap-8">
              {portfolio.slice(0, 4).map((p, i) => (
                <Reveal key={p.id} delay={i * 0.05} className="min-w-0">
                  <Link to={`/portfolio/${p.slug}`} className="group block min-w-0" data-testid={`portfolio-card-${p.slug}`}>
                    <div className="relative mb-3 aspect-[4/3] overflow-hidden rounded-2xl border border-border soft-shadow sm:mb-4 sm:aspect-[16/11] sm:rounded-3xl">
                      <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700" loading="lazy" />
                    </div>
                    <div className="flex min-w-0 items-start justify-between gap-1.5">
                      <div className="min-w-0"><h3 className="break-words font-display text-sm font-bold leading-tight sm:text-xl md:text-2xl">{p.title}</h3>
                        <p className="text-sm text-muted-foreground mt-1">{p.industry} — {p.year}</p></div>
                      <ArrowUpRight className="h-4 w-4 shrink-0 text-primary transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 sm:h-5 sm:w-5" />
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* WHY */}
      {why && (
        <section className="py-16 md:py-32" data-testid="why-section">
          <div className="mx-auto max-w-[1400px] px-5 md:px-10">
            <div className="flex flex-col items-center text-center mb-14">
              <SectionLabel num="03">Why Digitalis</SectionLabel>
              <h2 className="max-w-2xl font-display text-3xl font-extrabold leading-tight tracking-tightest sm:text-4xl md:text-5xl">{why.title}</h2>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4 lg:gap-6">
              {(why.items || []).map((it, i) => (
                <Reveal key={i} delay={i * 0.06} className="min-w-0">
                  <div className="h-full rounded-2xl border border-border bg-card p-4 transition-all hover:border-primary/40 hover:soft-shadow sm:rounded-3xl sm:p-6 lg:p-7">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-sm font-bold text-primary sm:h-10 sm:w-10 sm:rounded-xl sm:text-base lg:h-11 lg:w-11 lg:rounded-2xl lg:text-lg">{String(i + 1).padStart(2, "0")}</span>
                    <h3 className="mb-2 mt-4 break-words font-display text-base font-bold leading-tight sm:mt-5 sm:text-lg lg:text-xl">{it.title}</h3>
                    <p className="text-xs leading-relaxed text-muted-foreground sm:text-sm">{it.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PROCESS */}
      {process && (
        <section className="py-16 md:py-32 bg-surface" data-testid="process-section">
          <div className="mx-auto max-w-[1400px] px-5 md:px-10">
            <SectionLabel num="04">Proses</SectionLabel>
            <h2 className="mb-10 font-display text-3xl font-extrabold leading-tight tracking-tightest sm:text-4xl md:mb-14 md:text-5xl">{process.title}</h2>
            <div className="grid grid-cols-2 gap-3 min-[480px]:grid-cols-3 sm:gap-4 md:grid-cols-5">
              {(process.items || []).map((it, i) => (
                <Reveal key={i} delay={i * 0.06} className="min-w-0">
                  <div className="h-full rounded-2xl border border-border bg-card p-4 sm:rounded-3xl sm:p-5 lg:p-6">
                    <span className="font-display text-2xl font-extrabold text-primary sm:text-3xl">{it.step}</span>
                    <h3 className="mb-2 mt-3 break-words font-display text-base font-bold sm:text-lg">{it.title}</h3>
                    <p className="text-xs leading-relaxed text-muted-foreground sm:text-sm">{it.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* MICRO */}
      {micro && (
        <section className="py-16 md:py-32" data-testid="micro-section">
          <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <SectionLabel num="05">Micro Influencer</SectionLabel>
              <h2 className="font-display font-extrabold tracking-tightest leading-tight text-3xl md:text-4xl mb-5">{micro.title}</h2>
              <p className="text-muted-foreground text-lg mb-8 max-w-md">{micro.description}</p>
              <Btn to={micro.cta_link} variant="ghost">{micro.cta}</Btn>
            </div>
            <div className="lg:col-span-7">
              <div className="rounded-3xl overflow-hidden aspect-[16/10] soft-shadow border border-border">
                <img src={micro.image} alt="Micro influencer marketing Medan" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* LOCAL */}
      {local && (
        <section className="py-16 md:py-32" data-testid="local-section">
          <div className="mx-auto max-w-[1400px] px-5 md:px-10">
            <div className="rounded-[2rem] bg-primary text-primary-foreground p-10 md:p-16 grid lg:grid-cols-12 gap-10 items-center overflow-hidden relative">
              <div className="lg:col-span-7">
                <p className="overline text-[10px] opacity-70 mb-5">Medan, Sumatera Utara</p>
                <h2 className="font-display font-extrabold tracking-tightest leading-tight text-3xl md:text-5xl mb-5">{local.title}</h2>
                <p className="opacity-90 text-lg max-w-xl">{local.description}</p>
              </div>
              <div className="lg:col-span-5">
                <div className="rounded-3xl overflow-hidden aspect-square border border-white/20"><img src={local.image} alt="Digitalis studio Medan" className="w-full h-full object-cover" loading="lazy" /></div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* BLOG */}
      {posts.length > 0 && blogSec && (
        <section className="py-16 md:py-32 bg-surface" data-testid="blog-section">
          <div className="mx-auto max-w-[1400px] px-5 md:px-10">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
              <div><SectionLabel num="06">Insights</SectionLabel><h2 className="font-display text-3xl font-extrabold leading-tight tracking-tightest sm:text-4xl md:text-5xl">{blogSec.title}</h2></div>
              <Link to="/blog" className="link-underline flex min-h-11 items-center gap-1.5 text-sm font-semibold text-primary">Semua Artikel <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-8 min-[480px]:grid-cols-3 sm:gap-6 md:gap-8">
              {posts.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.05} className="min-w-0">
                  <Link to={`/blog/${p.slug}`} className="group block min-w-0" data-testid={`blog-card-${p.slug}`}>
                    <div className="relative mb-3 aspect-[4/3] overflow-hidden rounded-2xl border border-border soft-shadow sm:mb-5 sm:rounded-3xl"><img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700" loading="lazy" /></div>
                    <MetaTag className="mb-2 max-w-full truncate">{p.category}</MetaTag>
                    <h3 className="break-words font-display text-sm font-bold leading-tight transition-colors group-hover:text-primary sm:text-lg md:text-xl">{p.title}</h3>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {faqs.length > 0 && faqSec && (
        <section className="py-16 md:py-32" data-testid="faq-section">
          <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4"><SectionLabel num="07">FAQ</SectionLabel><h2 className="font-display font-extrabold tracking-tightest leading-tight text-4xl md:text-5xl">{faqSec.title}</h2></div>
            <div className="lg:col-span-8">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((f, i) => (
                  <AccordionItem key={i} value={`f${i}`} className="border border-border rounded-2xl px-5 mb-3 bg-card">
                    <AccordionTrigger className="text-left font-display font-semibold text-base md:text-lg py-5 hover:no-underline">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base leading-relaxed pb-5">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>
      )}

      {/* TEAM */}
      {teamSec && team.length > 0 && (
        <section className="py-16 md:py-32" data-testid="team-section">
          <div className="mx-auto max-w-[1400px] px-5 md:px-10">
            <div className="flex flex-col items-center text-center mb-14">
              <SectionLabel>Team Kerja</SectionLabel>
              <h2 className="font-display text-3xl font-extrabold leading-tight tracking-tightest sm:text-4xl md:text-5xl">{teamSec.title}</h2>
              {teamSec.subtitle && <p className="text-muted-foreground text-lg max-w-xl mt-4">{teamSec.subtitle}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-5">
              {team.slice(0, 4).map((m, i) => (
                <Reveal key={m.id} delay={(i % 3) * 0.06} className="min-w-0">
                  <div className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card transition-all hover:soft-shadow sm:rounded-3xl">
                    <div className="relative aspect-[3/4] overflow-hidden bg-[#0b0b0d]"><img src={m.photo} alt={m.name} className="h-full w-full object-contain object-top transition-transform duration-700 group-hover:scale-[1.02]" loading="lazy" />{m.is_placeholder && <span className="absolute left-2 top-2 rounded-full border border-white/15 bg-black/70 px-2 py-1 text-[8px] uppercase tracking-wider text-white/70">Profil contoh</span>}</div>
                    <div className="flex-1 p-3 sm:p-5">
                      <h3 className="break-words font-display text-base font-bold leading-tight sm:text-lg">{m.name}</h3>
                      <p className="mt-0.5 text-[10px] font-medium leading-snug text-primary sm:text-xs">{m.role}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <div className="mt-7 flex justify-center"><Btn to="/team" variant="ghost">Lihat Tim Lengkap</Btn></div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      {cta && (
        <section className="py-20 md:py-32" data-testid="cta-section">
          <div className="mx-auto max-w-[1400px] px-5 md:px-10">
            <div className="rounded-[2rem] border border-border bg-card soft-shadow p-12 md:p-20 text-center relative overflow-hidden">
              <div className="relative">
                <h2 className="font-display font-extrabold tracking-tightest leading-tight text-4xl md:text-6xl mb-6 max-w-3xl mx-auto">{cta.title}</h2>
                <p className="text-muted-foreground text-lg max-w-xl mx-auto mb-10">{cta.description}</p>
                <div className="flex flex-wrap gap-3 justify-center">
                  <Btn to={cta.cta_primary_link}>{cta.cta_primary}</Btn>
                  <Btn to={cta.cta_secondary_link} variant="ghost">{cta.cta_secondary}</Btn>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
